import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  CardMedia,
  Button,
  Chip,
  TextField,
  Rating,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ShopItem, ItemVariant } from '../../types/shop';

// Mock item data - in a real app this would come from an API
const mockItem: ShopItem = {
  id: '1',
  name: 'PEO Foundation T-Shirt',
  description:
    'Comfortable cotton t-shirt featuring the PEO Foundation logo. Made from 100% organic cotton, this premium t-shirt is perfect for everyday wear and proudly displays your PEO Foundation support. Available in multiple sizes and colors.',
  shortDescription: 'PEO Foundation branded t-shirt',
  price: 25.0,
  salePrice: 20.0,
  category: 'Clothing',
  tags: ['clothing', 'foundation', 'branded', 'cotton', 'organic'],
  images: [
    'https://prd.place/600/600?id=8',
    'https://prd.place/600/600?id=9',
    'https://prd.place/600/600?id=10',
  ],
  thumbnail: 'https://prd.place/600/600?id=8',
  inStock: true,
  variants: [
    {
      id: '1-1',
      itemId: '1',
      name: 'Small',
      sku: 'TS-S',
      price: 25.0,
      inStock: true,
      attributes: { size: 'S' },
    },
    {
      id: '1-2',
      itemId: '1',
      name: 'Medium',
      sku: 'TS-M',
      price: 25.0,
      inStock: true,
      attributes: { size: 'M' },
    },
    {
      id: '1-3',
      itemId: '1',
      name: 'Large',
      sku: 'TS-L',
      price: 25.0,
      inStock: true,
      attributes: { size: 'L' },
    },
    {
      id: '1-4',
      itemId: '1',
      name: 'X-Large',
      sku: 'TS-XL',
      price: 27.0,
      inStock: false,
      attributes: { size: 'XL' },
    },
  ],
  featured: true,
  createdAt: new Date('2024-01-01'),
  updatedAt: new Date('2024-01-01'),
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tabtab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ItemDetail() {
  const navigate = useNavigate();
  const [selectedVariant, setSelectedVariant] = useState<ItemVariant | null>(
    mockItem.variants[0]
  );
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  const handleVariantChange = (variant: ItemVariant) => {
    setSelectedVariant(variant);
    setQuantity(1); // Reset quantity when variant changes
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseInt(event.target.value);
    if (newQuantity > 0) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (!selectedVariant) return;

    // In a real app, this would add to cart context/state

    // Navigate to cart
    navigate('/shop/cart');
  };

  const handleBuyNow = () => {
    if (!selectedVariant) return;

    // In a real app, this would add to cart and navigate to checkout

    navigate('/shop/checkout');
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const currentPrice = selectedVariant?.price || mockItem.price;
  const isOnSale = mockItem.salePrice && mockItem.salePrice < currentPrice;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/shop')}
        sx={{ mb: 3 }}
      >
        Back to Shop
      </Button>

      <Grid container spacing={4}>
        {/* Product Images */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              height="500"
              image={mockItem.images[selectedImage]}
              alt={mockItem.name}
              sx={{ borderRadius: 2, objectFit: 'cover' }}
            />

            {/* Favorite Button */}
            <Button
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                backgroundColor: 'rgba(255,255,255,0.9)',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,1)',
                },
              }}
              onClick={toggleFavorite}
            >
              {isFavorite ? (
                <FavoriteIcon color="error" />
              ) : (
                <FavoriteBorderIcon />
              )}
            </Button>

            {/* Sale Badge */}
            {isOnSale && (
              <Chip
                label={`${Math.round(((currentPrice - (mockItem.salePrice || 0)) / currentPrice) * 100)}% OFF`}
                color="error"
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  fontWeight: 'bold',
                }}
              />
            )}
          </Box>

          {/* Thumbnail Images */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {mockItem.images.map((image, index) => (
              <CardMedia
                key={index}
                component="img"
                height="80"
                image={image}
                alt={`${mockItem.name} ${index + 1}`}
                sx={{
                  borderRadius: 1,
                  cursor: 'pointer',
                  border:
                    selectedImage === index
                      ? '2px solid #1976d2'
                      : '2px solid transparent',
                  opacity: selectedImage === index ? 1 : 0.7,
                  '&:hover': {
                    opacity: 1,
                  },
                }}
                onClick={() => setSelectedImage(index)}
              />
            ))}
          </Box>
        </Grid>

        {/* Product Info */}
        <Grid item xs={12} md={6}>
          <Box>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              {mockItem.name}
            </Typography>

            {/* Rating */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Rating value={4.5} precision={0.5} readOnly />
              <Typography variant="body2" sx={{ ml: 1 }}>
                (24 reviews)
              </Typography>
            </Box>

            {/* Price */}
            <Box sx={{ mb: 3 }}>
              {isOnSale ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant="h4"
                    color="error"
                    sx={{ fontWeight: 'bold' }}
                  >
                    ${mockItem.salePrice?.toFixed(2)}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      textDecoration: 'line-through',
                      color: 'text.secondary',
                    }}
                  >
                    ${currentPrice.toFixed(2)}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="h4"
                  color="primary"
                  sx={{ fontWeight: 'bold' }}
                >
                  ${currentPrice.toFixed(2)}
                </Typography>
              )}
            </Box>

            {/* Description */}
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
              {mockItem.description}
            </Typography>

            {/* Variants */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Size
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {mockItem.variants.map((variant) => (
                  <Chip
                    key={variant.id}
                    label={variant.name}
                    onClick={() => handleVariantChange(variant)}
                    variant={
                      selectedVariant?.id === variant.id ? 'filled' : 'outlined'
                    }
                    color={
                      selectedVariant?.id === variant.id ? 'primary' : 'default'
                    }
                    disabled={!variant.inStock}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>

            {/* Quantity */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quantity
              </Typography>
              <TextField
                type="number"
                value={quantity}
                onChange={handleQuantityChange}
                inputProps={{ min: 1, max: 99 }}
                sx={{ width: 100 }}
              />
            </Box>

            {/* Stock Status */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body1"
                color={selectedVariant?.inStock ? 'success.main' : 'error.main'}
                sx={{ fontWeight: 'bold' }}
              >
                {selectedVariant?.inStock ? 'In Stock' : 'Out of Stock'}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CartIcon />}
                onClick={handleAddToCart}
                disabled={!selectedVariant?.inStock}
                sx={{ flex: 1, py: 1.5, fontWeight: 'bold' }}
              >
                Add to Cart
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleBuyNow}
                disabled={!selectedVariant?.inStock}
                sx={{ flex: 1, py: 1.5, fontWeight: 'bold' }}
              >
                Buy Now
              </Button>
            </Box>

            {/* Tags */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Tags: {mockItem.tags.join(', ')}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Product Details Tabs */}
      <Box sx={{ mt: 6 }}>
        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Description" />
            <Tab label="Specifications" />
            <Tab label="Reviews" />
            <Tab label="Shipping & Returns" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              {mockItem.description}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.6 }}>
              This premium t-shirt is crafted from 100% organic cotton, ensuring
              both comfort and sustainability. The fabric is soft to the touch
              and breathable, making it perfect for everyday wear. The PEO
              Foundation logo is printed using eco-friendly inks that maintain
              their vibrant colors wash after wash.
            </Typography>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Material
                </Typography>
                <Typography variant="body1">100% Organic Cotton</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Weight
                </Typography>
                <Typography variant="body1">180 GSM</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Fit
                </Typography>
                <Typography variant="body1">Regular Fit</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Care Instructions
                </Typography>
                <Typography variant="body1">
                  Machine wash cold, tumble dry low
                </Typography>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Customer Reviews
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Rating value={4.5} precision={0.5} readOnly />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  4.5 out of 5
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Based on 24 reviews
              </Typography>
            </Box>

            {/* Mock Reviews */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Rating value={5} readOnly size="small" />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Sarah M. - Verified Buyer
                </Typography>
              </Box>
              <Typography variant="body1">
                "Love this t-shirt! The quality is excellent and it's so
                comfortable. The PEO Foundation logo looks great!"
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Rating value={4} readOnly size="small" />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Jennifer L. - Verified Buyer
                </Typography>
              </Box>
              <Typography variant="body1">
                "Great fit and comfortable material. Perfect for showing PEO
                pride!"
              </Typography>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>
              Shipping Information
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Free standard shipping on orders over $50. Standard shipping takes
              3-5 business days.
            </Typography>

            <Typography variant="h6" gutterBottom>
              Return Policy
            </Typography>
            <Typography variant="body1">
              We offer a 30-day return policy for all items in original
              condition. Return shipping is free for defective items.
            </Typography>
          </TabPanel>
        </Paper>
      </Box>
    </Container>
  );
}
